import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '200px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(0, 0, 0, 0) 100%)',
          padding: '60px 7.6923%',
          borderBottom: '1px solid var(--border-subtle)'
        }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              marginBottom: '30px',
              fontSize: '14px'
            }}>
              <ArrowLeft size={16} />
              Back to Home
            </Link>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <Shield size={40} color="var(--brand-primary)" />
              <h1 className="heading-1" style={{ margin: 0 }}>Privacy Policy</h1>
            </div>
            
            <p className="body-large" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              VDM Business Innovations
            </p>
            <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
              <strong>Effective Date:</strong> February 8, 2026 | <strong>Last Updated:</strong> February 8, 2026
            </p>
          </div>
        </section>

        {/* Content */}
        <section style={{ padding: '60px 7.6923%' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            {/* Section 1 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>1. Introduction</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                VDM Business Innovations ("we," "us," "our," or "Company") is committed to protecting the privacy and security of your personal data. This Privacy Policy explains how we collect, use, store, share, and protect your personal information in compliance with the Digital Personal Data Protection Act, 2023 (DPDP Act) and applicable Indian data protection laws.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                This policy applies to all personal data we process through our website (https://vdmbi.org), business operations, and services across our divisions including Zenthara (retail space leasing and consulting), Nuvora (environmental and waste management solutions), and related business activities.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                By accessing our website or engaging with our services, you acknowledge that you have read, understood, and agree to be bound by this Privacy Policy.
              </p>
            </div>

            {/* Section 2 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>2. Data Controller Information</h2>
              <div style={{ 
                background: 'rgba(0, 255, 209, 0.05)', 
                border: '1px solid var(--border-subtle)', 
                padding: '24px', 
                marginBottom: '16px' 
              }}>
                <p className="body-medium" style={{ color: 'var(--text-primary)', marginBottom: '8px' }}><strong>VDM Business Innovations</strong></p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Registered Address: Bengaluru, Karnataka, India</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Contact Email: sandeep@vdmbi.org</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Contact Phone: +91 8050518788</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>Website: https://vdmbi.org</p>
              </div>
            </div>

            {/* Section 3 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>3. Types of Personal Data We Collect</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                We collect personal data that is necessary and relevant for providing our services and conducting legitimate business operations.
              </p>
              
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>3.1 Information You Provide Directly</h3>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px', marginBottom: '20px' }}>
                <li>Contact Information: Name, email address, phone number, business address</li>
                <li>Business Information: Company name, designation, industry sector, business requirements</li>
                <li>Professional Details: Job title, department, professional background</li>
                <li>Communication Records: Correspondence through email, phone, WhatsApp</li>
                <li>Service Requests: Details about property requirements, project specifications</li>
                <li>Financial Information: Payment details, billing information, GST details</li>
              </ul>

              <h3 className="heading-3" style={{ marginBottom: '12px' }}>3.2 Information Collected Automatically</h3>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px', marginBottom: '20px' }}>
                <li>Website Usage Data: IP address, browser type, device information</li>
                <li>Cookies and Tracking: Session cookies, analytics cookies, preference settings</li>
                <li>Log Data: Access times, pages viewed, referring URLs, click patterns</li>
                <li>Technical Information: Device identifiers, connection information, geographic location</li>
              </ul>

              <h3 className="heading-3" style={{ marginBottom: '12px' }}>3.3 Information from Third Parties</h3>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px' }}>
                <li>Business information from publicly available sources (LinkedIn, company websites)</li>
                <li>References and background verification for business partnerships</li>
                <li>Information from mall management companies, property owners, and business partners</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>4. Purpose of Data Collection and Processing</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                We collect and process your personal data only for specified, explicit, and legitimate purposes:
              </p>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Service Delivery</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Providing retail space leasing, consulting services through Zenthara, environmental solutions through Nuvora, and managing client relationships.</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Business Operations</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Contract negotiation, invoice generation, payment processing, maintaining business records, and quality assurance.</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Communication</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Responding to inquiries, sending service updates, sharing relevant opportunities, and providing customer support.</p>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Legal and Compliance</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Complying with legal obligations, responding to legal processes, preventing fraud, and enforcing our terms of service.</p>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>5. Legal Basis for Processing</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                Under the DPDP Act, 2023, we process your personal data based on the following legal grounds:
              </p>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px' }}>
                <li><strong>Consent:</strong> Where you have provided explicit, informed, and freely given consent</li>
                <li><strong>Contractual Necessity:</strong> Processing required to perform a contract with you</li>
                <li><strong>Legal Obligation:</strong> Processing necessary to comply with applicable laws</li>
                <li><strong>Legitimate Interest:</strong> Processing necessary for legitimate business purposes</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>6. Data Minimization and Retention</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                We adhere to the principle of data minimization and retain your personal data only for as long as necessary.
              </p>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0, 255, 209, 0.1)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Data Category</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Retention Period</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>Justification</th>
                    </tr>
                  </thead>
                  <tbody style={{ color: 'var(--text-secondary)' }}>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Active Client Records</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Duration + 7 years</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Legal and tax compliance</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Inquiry Data</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>2 years from last contact</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Business development</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Financial Records</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>7 years from transaction</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Income Tax Act requirements</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Contract Documents</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>10 years from contract end</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Legal limitation periods</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Website Logs</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>90 days</td>
                      <td style={{ padding: '12px', borderBottom: '1px solid var(--border-subtle)' }}>Security and analytics</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 7 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>7. Your Rights as a Data Principal</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                Under the DPDP Act, 2023, you have the following rights:
              </p>
              
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Right to Access</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Obtain confirmation of whether we are processing your personal data and access a summary of such data.</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Right to Correction</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Request correction of inaccurate or incomplete personal data.</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Right to Erasure</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Request deletion of your personal data when no longer necessary or when consent is withdrawn.</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Right to Data Portability</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Receive your personal data in a structured, machine-readable format.</p>
                </div>
                <div style={{ borderLeft: '3px solid var(--brand-primary)', paddingLeft: '16px' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '4px' }}>Right to Grievance Redressal</h4>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Lodge a complaint with our designated Grievance Officer regarding any concerns.</p>
                </div>
              </div>
            </div>

            {/* Section 8 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>8. Consent Management</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                Where we rely on consent as the legal basis for processing, we ensure that consent is freely given, specific, informed, unambiguous, and verifiable.
              </p>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                <strong>Withdrawal of Consent:</strong> You have the right to withdraw your consent at any time by contacting us. You can opt out of marketing emails by clicking the "unsubscribe" link in any marketing email or contacting our Grievance Officer.
              </p>
            </div>

            {/* Section 9 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>9. Data Security Measures</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '20px' }}>
                We implement reasonable and appropriate technical and organizational security measures:
              </p>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>Technical Safeguards</h4>
                  <ul style={{ color: 'var(--text-secondary)', fontSize: '14px', paddingLeft: '18px', lineHeight: '1.8' }}>
                    <li>Encryption of data in transit (TLS/SSL)</li>
                    <li>Secure authentication and access controls</li>
                    <li>Regular security updates and patching</li>
                    <li>Firewalls and intrusion detection</li>
                  </ul>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', border: '1px solid var(--border-subtle)' }}>
                  <h4 style={{ color: 'var(--text-primary)', marginBottom: '12px' }}>Organizational Safeguards</h4>
                  <ul style={{ color: 'var(--text-secondary)', fontSize: '14px', paddingLeft: '18px', lineHeight: '1.8' }}>
                    <li>Access limited to authorized personnel</li>
                    <li>Confidentiality agreements</li>
                    <li>Regular privacy awareness training</li>
                    <li>Incident response procedures</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 10-11 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>10. Data Breach Notification</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                In the event of a data breach that is likely to cause harm, we will notify the Data Protection Board of India within 72 hours, notify affected individuals promptly, and take immediate corrective actions.
              </p>
            </div>

            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>11. Disclosure and Sharing of Personal Data</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                We do not sell, rent, or trade your personal data to third parties. We may share information with:
              </p>
              <ul style={{ color: 'var(--text-secondary)', lineHeight: '1.8', paddingLeft: '24px' }}>
                <li>Service providers and business partners (under contractual obligations)</li>
                <li>Mall management companies and property owners</li>
                <li>Legal authorities when required by law</li>
                <li>Acquiring entities in case of business transfers (with notification)</li>
              </ul>
            </div>

            {/* Section 12-13 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>12. Cross-Border Data Transfers</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                We process and store personal data primarily within India. Any transfer to countries outside India will only be to approved countries with adequate security measures and your explicit consent where required.
              </p>
            </div>

            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>13. Cookies and Tracking Technologies</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '16px' }}>
                Our website uses cookies including essential cookies, performance cookies, functional cookies, and analytics cookies. You can control cookie settings through your browser preferences.
              </p>
            </div>

            {/* Section 14 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>14. Children's Privacy</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Our services are intended for business-to-business (B2B) use and are not directed at individuals under the age of 18. We do not knowingly collect personal data from children.
              </p>
            </div>

            {/* Section 15 - Grievance */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>15. Grievance Redressal Mechanism</h2>
              <div style={{ 
                background: 'rgba(0, 255, 209, 0.05)', 
                border: '1px solid var(--brand-primary)', 
                padding: '24px' 
              }}>
                <h4 style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>Grievance Officer</h4>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  <Mail size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Email: sandeep@vdmbi.org
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  <Phone size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Phone: +91 8050518788
                </p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Address: VDM Business Innovations, Bengaluru, Karnataka, India
                </p>
                <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                  Response Time: We will acknowledge your grievance within 72 hours and provide a resolution within 30 days.
                </p>
              </div>
            </div>

            {/* Section 16-17 */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>16. Third-Party Links</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                Our website may contain links to third-party websites. This Privacy Policy applies only to our website and services. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </div>

            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>17. Updates to This Privacy Policy</h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last Updated" date, post the revised policy with prominent notice, and notify you via email if applicable.
              </p>
            </div>

            {/* Contact Section */}
            <div style={{ marginBottom: '40px' }}>
              <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>20. Contact Us</h2>
              <div style={{ 
                background: 'rgba(255,255,255,0.03)', 
                border: '1px solid var(--border-subtle)', 
                padding: '24px' 
              }}>
                <p className="body-medium" style={{ color: 'var(--text-primary)', marginBottom: '16px' }}><strong>VDM Business Innovations</strong></p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Address: Bengaluru, Karnataka, India</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Email: sandeep@vdmbi.org</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Phone: +91 8050518788</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>Website: https://vdmbi.org</p>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>Business Hours: Monday to Saturday, 9:00 AM to 6:00 PM IST</p>
              </div>
            </div>

            {/* Consent Acknowledgment */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.1) 0%, rgba(0, 255, 209, 0.02) 100%)', 
              border: '1px solid var(--brand-primary)', 
              padding: '30px',
              marginTop: '40px'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>Consent Acknowledgment</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                By accessing our website, using our services, or providing your personal information to VDM Business Innovations, you acknowledge that you have read and understood this Privacy Policy, consent to the collection and processing of your personal data as described herein, and understand your rights as a data principal under the DPDP Act, 2023.
              </p>
            </div>

            {/* Document Info */}
            <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid var(--border-subtle)' }}>
              <p className="body-small" style={{ color: 'var(--text-muted)' }}>
                Document Version: 1.0 | Policy Owner: VDM Business Innovations | Review Cycle: Annual | Next Review Date: February 2027
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
